import { MatrixType } from '../url/matrixMapping';
import { URLManager } from '../url/URLManager';
import { matrixToView, viewToMatrix, MATRIX_DEFINITIONS, MATRIX_DISPLAY_NAMES } from '../url/matrixMapping';

type MatrixChangeListener = () => void;

export interface MatrixMenuState {
  selectedMatrix: MatrixType;
  isUpdating: boolean;
  error?: string;
  lastUserSelection?: {
    matrix: MatrixType;
    timestamp: number;
  };
}

export interface MatrixValidationResult {
  isValid: boolean;
  matrix: MatrixType;
  error?: string;
  fallbackMatrix?: MatrixType;
}

export class MatrixSelectionManager {
  private static instance: MatrixSelectionManager;
  private urlManager: URLManager;
  private menuState: MatrixMenuState;
  private readonly STORAGE_KEY = 'railmapper_selected_matrix';
  private readonly DEFAULT_MATRIX: MatrixType = 'cities';
  private listeners: MatrixChangeListener[] = [];

  private constructor() {
    this.urlManager = URLManager.getInstance();
    this.menuState = {
      selectedMatrix: this.getInitialMatrix(),
      isUpdating: false
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY) {
        const newMatrix = event.newValue as MatrixType;
        if (newMatrix && this.validateMatrixType(newMatrix).isValid) {
          this.updateMenuState(newMatrix);
        }
      }
    });
  }

  static getInstance(): MatrixSelectionManager {
    if (!MatrixSelectionManager.instance) {
      MatrixSelectionManager.instance = new MatrixSelectionManager();
    }
    return MatrixSelectionManager.instance;
  }

  private getInitialMatrix(): MatrixType {
    // Try to get from URL first
    const currentView = this.urlManager.getCurrentView();
    if (currentView) {
      const matrixType = this.getMatrixFromView(currentView);
      if (matrixType) {
        return matrixType;
      }
    }

    // Try to get from localStorage
    const savedMatrix = localStorage.getItem(this.STORAGE_KEY) as MatrixType;
    if (savedMatrix && this.validateMatrixType(savedMatrix).isValid) {
      return savedMatrix;
    }

    // Fall back to default
    return this.DEFAULT_MATRIX;
  }

  private updateMenuState(newMatrix: MatrixType, isUserAction: boolean = false) {
    this.menuState = {
      ...this.menuState,
      selectedMatrix: newMatrix,
      ...(isUserAction && {
        lastUserSelection: {
          matrix: newMatrix,
          timestamp: Date.now()
        }
      }),
      isUpdating: false,
      error: undefined
    };

    // Update localStorage
    localStorage.setItem(this.STORAGE_KEY, newMatrix);

    // Update URL if we're on the results page
    if (this.urlManager.getCurrentPage() === 'results') {
      this.updateURL(newMatrix);
    }

    // Notify listeners
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  addChangeListener(listener: MatrixChangeListener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // New centralized matrix/view conversion methods
  getMatrixFromView(view: string): MatrixType | null {
    const matrixType = viewToMatrix(view);
    const isValid = matrixType && this.validateMatrixType(matrixType).isValid;
    return matrixType && isValid ? matrixType : null;
  }

  getViewFromMatrix(matrix: MatrixType): string | null {
    return matrixToView(matrix);
  }

  updateFromView(view: string | null) {
    if (!view) return;
    
    // Only update if the view actually represents a different matrix type
    const matrixType = this.getMatrixFromView(view);
    if (matrixType && matrixType !== this.menuState.selectedMatrix) {
      this.updateMenuState(matrixType, false);
    }
  }

  updateURL(matrix: MatrixType = this.getCurrentMatrix()) {
    const view = this.getViewFromMatrix(matrix);
    if (view) {
      this.urlManager.updateURL(
        this.urlManager.getCurrentIdentifier(),
        'results',
        view,
        'user',
        'matrix_selector'
      );
    }
  }

  syncWithURL(view: string | null) {
    this.updateFromView(view);
    this.updateURL(this.getCurrentMatrix());
  }

  public handleMenuSelection(newMatrix: MatrixType): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const validation = this.validateMatrixType(newMatrix);
        if (!validation.isValid) {
          throw new Error(validation.error || 'Invalid matrix type');
        }

        this.menuState.isUpdating = true;
        this.menuState.error = undefined;
        this.notifyListeners();

        this.updateMenuState(newMatrix, true);
        resolve();
      } catch (error) {
        this.menuState = {
          ...this.menuState,
          error: error instanceof Error ? error.message : 'Failed to update matrix selection',
          isUpdating: false
        };
        this.notifyListeners();
        reject(error);
      }
    });
  }

  validateMatrixType(type: string): MatrixValidationResult {
    if (type in MATRIX_DEFINITIONS) {
      const def = MATRIX_DEFINITIONS[type as MatrixType];
      // Only allow matrices that are allowed in results when in results page
      if (this.urlManager.getCurrentPage() === 'results' && !def.allowInResults) {
        return {
          isValid: false,
          matrix: this.DEFAULT_MATRIX,
          error: `Matrix type ${type} is not allowed in results view`,
          fallbackMatrix: this.DEFAULT_MATRIX
        };
      }
      return {
        isValid: true,
        matrix: type as MatrixType
      };
    }

    return {
      isValid: false,
      matrix: this.DEFAULT_MATRIX,
      error: `Invalid matrix type: ${type}`,
      fallbackMatrix: this.DEFAULT_MATRIX
    };
  }

  getMenuState(): MatrixMenuState {
    return { ...this.menuState };
  }

  getCurrentMatrix(): MatrixType {
    return this.menuState.selectedMatrix;
  }

  getAvailableMatrices(): MatrixType[] {
    const isResultsPage = this.urlManager.getCurrentPage() === 'results';
    return Object.keys(MATRIX_DEFINITIONS)
      .filter(matrix => !isResultsPage || MATRIX_DEFINITIONS[matrix as MatrixType].allowInResults)
      .map(matrix => matrix as MatrixType);
  }

  getMatrixDisplayName(matrix: MatrixType): string {
    return MATRIX_DISPLAY_NAMES[matrix] || matrix;
  }
} 